import { createSelector } from 'reselect'
import { isHoldReservationStillActive } from 'businessTraveller/utils/flightHoldDeadline'
import { getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { getFlightItems } from 'checkout/selectors/view/flights'
import { selectBusinessTravellerCurrentApprovalRequest } from './selectBusinessTravellerCurrentApprovalRequest'

const selectBookAndHoldEnabled = (state: App.State) =>
  getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.lebtFlightBookAndHold)

type HeldFlightReservation = { reservationId: string; ticketingDeadline: string }

/**
 * The still-held flight fares keyed by cart item id — a fare only qualifies while it has a reservation id
 * and its ticketing deadline is still ahead; past the deadline the airline hold is gone. The reuse map,
 * the held-item set and the deadline all derive from this, so they can never disagree.
 */
const selectHeldFlightReservationByCartItemId = createSelector(
  selectBusinessTravellerCurrentApprovalRequest,
  selectBookAndHoldEnabled,
  (currentApprovalRequest, bookAndHoldEnabled): Record<string, HeldFlightReservation> => {
    const heldByCartItemId: Record<string, HeldFlightReservation> = {}
    if (!bookAndHoldEnabled) {
      return heldByCartItemId
    }
    const now = Date.now()
    for (const tripRequestItem of currentApprovalRequest?.tripRequestItems ?? []) {
      const details = tripRequestItem.details
      if (
        tripRequestItem.type === 'flight' &&
        details?.reservationId &&
        details.ticketingDeadline &&
        isHoldReservationStillActive(details.ticketingDeadline, now)
      ) {
        heldByCartItemId[tripRequestItem.externalCartItemId] = {
          reservationId: details.reservationId,
          ticketingDeadline: details.ticketingDeadline,
        }
      }
    }
    return heldByCartItemId
  },
)

/** Held flight reservation id keyed by cart item id, for checkout reuse. */
export const selectHeldReservationIdByCartItemId = createSelector(
  selectHeldFlightReservationByCartItemId,
  (heldByCartItemId): Record<string, string> =>
    Object.fromEntries(Object.entries(heldByCartItemId).map(([cartItemId, held]) => [cartItemId, held.reservationId])),
)

/** The set of cart item ids whose flight fare was held at submit. */
export const selectHeldCartItemIds = createSelector(
  selectHeldFlightReservationByCartItemId,
  (heldByCartItemId): Set<string> => new Set(Object.keys(heldByCartItemId)),
)

/** True when a flight fare in the cart is being reused from a still-held PNR. */
export const selectIsHeldFlightReservationReused = createSelector(
  selectHeldCartItemIds,
  (heldCartItemIds): boolean => heldCartItemIds.size > 0,
)

/**
 * The ticketing deadline of a held fare that is still in the cart (absolute UTC ISO), or undefined when none.
 * Scoped to the live cart item so re-selecting the flight (fresh item id, no reuse) drops the deadline and the
 * staleness timer resumes — the same cart-item join the reuse map uses.
 */
export const selectHeldFlightTicketingDeadline = createSelector(
  getFlightItems,
  selectHeldFlightReservationByCartItemId,
  (flightItems, heldByCartItemId): string | undefined => {
    for (const flightItem of flightItems) {
      const held = heldByCartItemId[flightItem.itemId]
      if (held) {
        return held.ticketingDeadline
      }
    }
    return undefined
  },
)
